import React, { useState, useRef, useEffect, useMemo } from 'react';
import { EntryFormProps, Entry } from '../types';
import { FeedFields } from './StructuredEntryFields';
import { GUARDIAN_SUMMARY_NOTE } from '../../../../constants/familyPortal';
import { useObservedTime } from '../ObservedTimeContext';
import { useChartForm } from '../ChartFormContext';
import { FORM_ERROR, REQUIRED, validateEntryForm, EntryErrors } from '../model/entryValidation';
import { COPY_LAST_TYPES } from '../model/entryPayload';
import { isSupervisoryVisit } from '../model/entryMeta';
import { emptyOutput, emptyRespiratory, emptySeizure, emptySupervisory, emptyTherapy, emptyAideTasks, EntryFormBundle } from '../model/entryForms';
import ObservedAtField, { LATE_NOTE, visitHasEnded } from './forms/ObservedAtField';
import { CopyLastButton } from './forms/SmallControls';
import VitalsFields from './forms/VitalsFields';
import MeasurementsFields from './forms/MeasurementsFields';
import MarFields from './forms/MarFields';
import NoteFields from './forms/NoteFields';
import { IncidentFields, WoundFields } from './forms/WoundIncidentFields';
import { OutputFields, RespiratoryFields, SeizureFields } from './forms/ObservationFields';
import SupervisoryNoteFields from './forms/SupervisoryNoteFields';
import TherapyTreatmentFields from './forms/TherapyTreatmentFields';
import AideTasksFields from './forms/AideTasksFields';
import TemplateNoteFields from './forms/TemplateNoteFields';
import { emptyTemplateNote, noteTemplateFor } from '../model/noteTemplates';
import { TextAreaField } from './forms/fieldParts';

/** Types whose own time field is the observed time (no separate "Observed at") */
const OWN_TIME_TYPES = new Set(['mar', 'seizure']);

type FormDataProps = Pick<EntryFormProps, 'vitalFormData' | 'marFormData' | 'feedFormData' | 'notesFormData' | 'woundFormData'
  | 'incidentFormData' | 'summaryFormData' | 'measurementsFormData' | 'patientMedications'>;

/**
 * Phase 18 M1: errors of the open form (client validation + the server's ENTRY_INVALID fields).
 * Save stays disabled while this is not empty. Works without the chart context (props only).
 */
export const useEntryFormErrors = (type: string | null | undefined, props: FormDataProps): EntryErrors => {
  const chart = useChartForm();
  if (!type) return {};
  if (chart) return { ...chart.errorsFor(type), ...chart.serverFieldErrors };
  const bundle: EntryFormBundle = {
    vitals: props.vitalFormData,
    mar: props.marFormData,
    feed: props.feedFormData,
    notes: props.notesFormData,
    wound: props.woundFormData,
    incident: props.incidentFormData,
    summary: props.summaryFormData,
    measurements: props.measurementsFormData,
    respiratory: emptyRespiratory(),
    seizure: emptySeizure(''),
    output: emptyOutput(),
    supervisory: emptySupervisory(),
    therapy: emptyTherapy(),
    aidetasks: emptyAideTasks(),
    templatenote: emptyTemplateNote()
  };
  return validateEntryForm(type, bundle, { medications: props.patientMedications || [] });
};

const EntryForm: React.FC<EntryFormProps & { isReadOnly?: boolean }> = ({
  selectedEntryType,
  vitalFormData,
  setVitalFormData,
  marFormData,
  setMarFormData,
  feedFormData,
  setFeedFormData,
  notesFormData,
  setNotesFormData,
  woundFormData,
  setWoundFormData,
  incidentFormData,
  setIncidentFormData,
  summaryFormData,
  setSummaryFormData,
  measurementsFormData,
  setMeasurementsFormData,
  editingEntryId,
  isMobile,
  patientMedications = [],
  isReadOnly = false,
  observedTime: observedTimeProp,
  setObservedTime: setObservedTimeProp,
  observedError: observedErrorProp
}) => {
  const observedCtx = useObservedTime();
  const chart = useChartForm();
  const observedTime = observedTimeProp ?? observedCtx?.observedTime;
  const setObservedTime = setObservedTimeProp ?? observedCtx?.setObservedTime;
  const observedError = observedErrorProp ?? observedCtx?.observedError ?? null;
  // Edits keep the entry's observed time (MAR and seizure change it through their own time)
  const allowObserved = !editingEntryId;
  // "Required" messages wait for the first change; range and format problems show at once
  const [touched, setTouched] = useState(false);
  const [copyNote, setCopyNote] = useState<string | null>(null);
  const firstInputRef = useRef<HTMLInputElement>(null);
  const firstTextAreaRef = useRef<HTMLTextAreaElement>(null);
  const fieldsRef = useRef<HTMLDivElement>(null);
  const visitEntries: Entry[] = useMemo(() => chart?.visitEntries || [], [chart?.visitEntries]);

  // Phase 18U U3: focus the first field of every entry type when the form opens (the observed
  // time is skipped: it defaults to now and is rarely changed)
  useEffect(() => {
    if (isReadOnly) return;
    const preferred = selectedEntryType === 'vitals' || selectedEntryType === 'measurements' ? firstInputRef.current : firstTextAreaRef.current;
    const first = fieldsRef.current?.querySelector<HTMLElement>('select:not([disabled]), input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), [role="radio"]');
    (preferred || first)?.focus();
  }, [selectedEntryType, editingEntryId, isReadOnly]);

  // Client validation (Phase 18 M1) merged with the server's 400 ENTRY_INVALID fields
  const errors = useEntryFormErrors(selectedEntryType, {
    vitalFormData, marFormData, feedFormData, notesFormData, woundFormData, incidentFormData, summaryFormData, measurementsFormData, patientMedications
  });
  const hasServerErrors = Object.keys(chart?.serverFieldErrors || {}).length > 0;
  const showRequired = touched || hasServerErrors;

  /** Wrap a setter so the first change shows "Required" messages */
  const touch = <T,>(setter: (next: T) => void) => (next: T) => {
    setTouched(true);
    setCopyNote(null);
    setter(next);
  };
  const style = { isMobile, disabled: isReadOnly, errors, showRequired };

  const renderSelectedForm = () => {
    switch (selectedEntryType) {
      case 'vitals':
        return <VitalsFields {...style} firstRef={firstInputRef} value={vitalFormData} onChange={touch(setVitalFormData)} />;
      case 'measurements':
        return <MeasurementsFields {...style} firstRef={firstInputRef} value={measurementsFormData} onChange={touch(setMeasurementsFormData)} />;
      case 'mar':
        return (
          <MarFields
            {...style}
            value={marFormData}
            onChange={touch(setMarFormData)}
            medications={patientMedications}
            lastGivenByMedication={chart?.lastGivenByMedication}
            followUpEntry={marFormData.followUpOf ? visitEntries.find((e) => String(e.id) === marFormData.followUpOf) : undefined}
          />
        );
      case 'feed':
        return <FeedFields value={feedFormData} onChange={touch(setFeedFormData)} isMobile={isMobile} disabled={isReadOnly} errors={errors} />;
      case 'notes':
        return <NoteFields {...style} firstRef={firstTextAreaRef} value={notesFormData} onChange={touch(setNotesFormData)} />;
      case 'wound':
        return <WoundFields {...style} firstRef={firstTextAreaRef} value={woundFormData} onChange={touch(setWoundFormData)} />;
      case 'incident':
        return <IncidentFields {...style} firstRef={firstTextAreaRef} value={incidentFormData} onChange={touch(setIncidentFormData)} />;
      case 'summary':
        return (
          <div className="space-y-1">
            {/* Phase 12 (FAM-03): the visit summary is the only chart text guardians can read */}
            <p className="text-xs text-slate-600">{GUARDIAN_SUMMARY_NOTE}</p>
            <TextAreaField {...style} id="summary-text" label="Summary" required inputRef={firstTextAreaRef} rows={isMobile ? 12 : 15}
              labelClassName="text-slate-700" placeholder="Visit summary, findings, plan..."
              value={summaryFormData} onChange={touch(setSummaryFormData)} error={errors.note} />
          </div>
        );
      case 'respiratory':
        return chart ? <RespiratoryFields {...style} value={chart.respiratoryFormData} onChange={touch(chart.setRespiratoryFormData)} /> : null;
      case 'seizure':
        return chart ? (
          <SeizureFields
            {...style}
            value={chart.seizureFormData}
            onChange={touch(chart.setSeizureFormData)}
            medicationEntries={visitEntries.filter((e) => e.type === 'mar' && !e.invalidated)}
          />
        ) : null;
      case 'output':
        return chart ? <OutputFields {...style} value={chart.outputFormData} onChange={touch(chart.setOutputFormData)} /> : null;
      case 'supervisory':
        // F21: only on a supervisory visit (the server answers 400 ENTRY_NOT_ALLOWED otherwise)
        if (!chart) return null;
        if (!editingEntryId && !isSupervisoryVisit(chart.visit?.visitType)) {
          return <p className="text-sm text-gray-600" data-testid="supervisory-not-allowed">A supervisory note can only be added on a supervisory visit.</p>;
        }
        return <SupervisoryNoteFields {...style} value={chart.supervisoryFormData} onChange={touch(chart.setSupervisoryFormData)} currentUserId={chart.currentUserId} />;
      case 'therapy':
        // Phase 20C: PT / OT / ST visits only (the server answers 400 ENTRY_NOT_ALLOWED otherwise)
        if (!chart?.therapyFormData || !chart.setTherapyFormData) return null;
        return (
          <TherapyTreatmentFields {...style} value={chart.therapyFormData} onChange={touch(chart.setTherapyFormData)}
            discipline={chart.visit?.discipline} patientId={chart.patientId} />
        );
      case 'aidetasks':
        // Phase 20D: aide and attendant visits only (the server answers 400 ENTRY_NOT_ALLOWED otherwise)
        if (!chart?.aideTasksFormData || !chart.setAideTasksFormData) return null;
        return (
          <AideTasksFields {...style} value={chart.aideTasksFormData} onChange={touch(chart.setAideTasksFormData)}
            discipline={chart.visit?.discipline} patientId={chart.patientId} />
        );
      default: {
        // Phase 20E: note templates (social work, respiratory treatment, nutrition, behavioral session)
        const template = noteTemplateFor(selectedEntryType);
        if (template && chart?.templateNoteFormData && chart.setTemplateNoteFormData) {
          return (
            <TemplateNoteFields {...style} template={template} value={chart.templateNoteFormData}
              onChange={touch(chart.setTemplateNoteFormData)} discipline={chart.visit?.discipline} />
          );
        }
        return (
          <div className="text-gray-500 text-sm text-center py-4">
            Select an entry type to begin.
          </div>
        );
      }
    }
  };

  // Phase 16C / 18 M8: every entry records when the care happened; MAR and seizure use their own time
  const showObserved = allowObserved && Boolean(setObservedTime) && selectedEntryType && !OWN_TIME_TYPES.has(selectedEntryType);
  const showCopyLast = Boolean(chart && !editingEntryId && !isReadOnly && selectedEntryType && (COPY_LAST_TYPES as readonly string[]).includes(selectedEntryType));
  const formError = errors[FORM_ERROR];
  const missingRequired = Object.values(errors).some((m) => m === REQUIRED);
  const visitWin = chart?.visitWindow;
  // Phase 18U U6: "Copy last" chosen in the Add sheet: the same reminder as the in-form button
  const tileCopyNote = !touched && !editingEntryId && chart?.copiedFromLast && chart.copiedFromLast === selectedEntryType
    ? 'Copied from the last entry. Check before saving.' : null;
  return (
    <div className="w-full">
      {(showObserved || showCopyLast) && (
        <div className="mb-1 flex flex-wrap items-start justify-between gap-2">
          {showObserved ? (
            <ObservedAtField value={observedTime || ''} onChange={(v) => setObservedTime?.(v)} error={observedError} disabled={isReadOnly} visitWindow={visitWin} touch={isMobile} />
          ) : <span />}
          {showCopyLast && (
            <div className="flex items-center gap-2">
              {(copyNote || tileCopyNote) && <span className="text-xs text-gray-600" role="status">{copyNote || tileCopyNote}</span>}
              <CopyLastButton
                disabled={!chart?.hasLast(selectedEntryType as string)}
                onClick={() => {
                  const copied = chart?.copyLast(selectedEntryType as string);
                  setCopyNote(copied ? 'Copied from the last entry. Check before saving.' : 'No earlier entry in this visit.');
                }}
              />
            </div>
          )}
        </div>
      )}
      {selectedEntryType && OWN_TIME_TYPES.has(selectedEntryType) && observedError && <p role="alert" className="mb-2 text-xs text-red-700">{observedError}</p>}
      {selectedEntryType && OWN_TIME_TYPES.has(selectedEntryType) && allowObserved && visitHasEnded(visitWin) && (
        <p className="mb-2 text-xs text-amber-700" data-testid="late-entry-note">{LATE_NOTE}</p>
      )}
      <div ref={fieldsRef}>{renderSelectedForm()}</div>
      {(formError && (touched || hasServerErrors)) && <p className="mt-1 text-xs text-red-600" data-testid="entry-form-error">{formError}</p>}
      {missingRequired && !isReadOnly && (
        <p className="mt-1 text-xs text-gray-500" data-testid="entry-required-hint">Fields marked * are required.</p>
      )}
    </div>
  );
};

export default EntryForm;
